import React, { useState, useEffect, useContext } from 'react';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { Tooltip, IconButton } from '@mui/material';
import { Delete, Restore } from '@mui/icons-material';

import axiosInstance from '../../api';
import StyledCard from '../../components/card/StyledCard';
import { authContext } from '../../routes';
import SERVER_API_URL from '../../config';
import LocaleText from '../../utils/localText';
import DeleteDialog from '../common/PermanentDeleteDialog';
import RestoreDialog from '../common/RestoreDialog';
import CustomSnackbar from '../../components/alerts/CustomSnackbar';
import { ethDate } from '../../utils/ethDate';

export default function DeletedDistrictList() {
  const [districtList, setDistrictList] = useState([]);
  const [openDelete, setOpenDelete] = useState(false);
  const [openRestore, setOpenRestore] = useState(false);
  const [selectedDistrict, setSelectedDistrict] = useState();
  const [notification, setNotification] = useState({});
  const { user } = useContext(authContext);

  const columns = [
    {
      headerClassName: 'super-app-theme--header',
      field: 'name',
      width: 200,
      headerName: 'የወረዳ ስም',
      valueGetter: (params) => {
        const { id, value } = params;
        const originalName = value.slice(0, value.length - id.length);
        return originalName;
      },
    },
    { headerClassName: 'super-app-theme--header', field: 'description', width: 200, headerName: 'የወረዳ መግለጫ' },
    { headerClassName: 'super-app-theme--header', field: 'history', width: 100, headerName: 'ታሪክ' },
    { headerClassName: 'super-app-theme--header', field: 'phone', width: 150, headerName: 'ስልክ ቁጥር' },
    { headerClassName: 'super-app-theme--header', field: 'email', width: 200, headerName: 'ኢሜይል' },
    {
      headerClassName: 'super-app-theme--header',
      field: 'churchCount',
      width: 100,
      headerName: 'አጥቢያ',
    },
    { headerClassName: 'super-app-theme--header', field: 'usersCount', width: 120, headerName: 'ተከታዮች' },
    {
      headerClassName: 'super-app-theme--header', field: 'actions',
      width: 100,
      headerName: 'ድርጊቶች',
      renderCell: (params) => {
        const handleDelete = async () => {
          try {
            const response = await axiosInstance.get(`${SERVER_API_URL}/district/findOneById/${params.id}`);
            if (response.status === 200) {
              setSelectedDistrict(response.data);
              setOpenDelete(true);
            }
          } catch (error) {
            // console.log(error)
          }
        };
        const handleRestore = async () => {
          try {
            const response = await axiosInstance.get(`${SERVER_API_URL}/district/findOneById/${params.id}`);
            if (response.status === 200) {
              setSelectedDistrict(response.data);
              setOpenRestore(true);
            }
          } catch (error) {
            // console.log(error)
          }
        };
        return (
          <>
            <Tooltip title="በቋሚነት ሰርዝ" color="error" arrow>
              <IconButton disabled={!(user && user.role.permissions.district.delete)} onClick={handleDelete}>
                <Delete sx={{ fontSize: '18px' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="ፋይሉን መልስ" color="success" arrow>
              <IconButton disabled={!(user && user.role.permissions.district.delete)} onClick={handleRestore}>
                <Restore sx={{ fontSize: '18px' }} />
              </IconButton>
            </Tooltip>
          </>
        );
      },
    },
    {
      headerClassName: 'super-app-theme--header',
      field: 'createdAt',
      headerName: 'የተመዘገበበት ቀን',
      width: 180,
      valueGetter: (params) => ethDate(params.value),
    },
    {
      headerClassName: 'super-app-theme--header',
      field: 'updatedAt',
      headerName: 'የተስተካከለበት ቀን',
      width: 180,
      valueGetter: (params) => ethDate(params.value),
    },
  ];

  const handleDeleteOk = async () => {
    try {
      const response = await axiosInstance.delete(`${SERVER_API_URL}/district/deleteOneById/${selectedDistrict.id}`);
      if (response.status === 200) {
        getAllDistrict();
        setOpenDelete(false);
      }
    } catch (error) {
      // console.log(error)
    }
  };
  const handleRestoreOk = async () => {
    try {
      const response = await axiosInstance.patch(`${SERVER_API_URL}/district/restoreOneById/${selectedDistrict.id}`);
      if (response.status === 200) {
        getAllDistrict();
      }
    } catch (error) {
      if (error.response.status === 406) {
        notification.message = 'በዚህ ወረዳ ስም የተመዘገበ አለ።';
        notification.severity = 'error';
      }
    }
    setOpenRestore(false);
  };

  const getAllDistrict = async () => {
    try {
      const response = await axiosInstance.get(`${SERVER_API_URL}/district/findAllDeleted`);

      if (response.status === 200) {
        setDistrictList(response.data);
      }
    } catch (error) {
      // console.log(error);
    }
  };

  useEffect(() => {
    getAllDistrict();
  }, []);

  return (
    <>
      <StyledCard sx={{
        '& .super-app-theme--header': {
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? theme.palette.primary.darkMode.lighter : theme.palette.grey[400],
        }
      }}>
        {notification.message && <CustomSnackbar notification={notification} setNotification={setNotification} />}
        <DataGrid
          sx={{ padding: 2, '& .MuiDataGrid-row': { backgroundColor: (theme) => theme.palette.mode === 'dark' ? "" : theme.palette.error.lighter, borderWidth: 0.5, borderTopColor: (theme) => theme.palette.error.darker, borderTopStyle: 'dashed' } }}
          rows={districtList}
          columns={columns}
          pageSize={12}
          initialState={{
            filter: {
              filterModel: {
                items: [],
                quickFilterValues: [''],
              },
            },
            pagination: { pageSize: 10 },
          }}
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 500 },
            },
          }}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          disableSelectionOnClickCell
          disableColumnFilter
          localeText={LocaleText.pagination}
          getRowStyle={() => ({ background: 'red' })}
        />
      </StyledCard>
      {selectedDistrict && (
        <DeleteDialog
          openDelete={openDelete}
          setOpenDelete={setOpenDelete}
          nameToBeDeleted={selectedDistrict.name.slice(0, selectedDistrict.name.length - selectedDistrict.id.length)}
          nameType="ወረዳ"
          message="አብያተ-ክርስቲያኖች እና ተጠቃሚዎች"
          handleOk={handleDeleteOk}
        />
      )}
      {selectedDistrict && (
        <RestoreDialog
          openRestore={openRestore}
          setOpenRestore={setOpenRestore}
          nameToBeRestored={selectedDistrict.name.slice(0, selectedDistrict.name.length - selectedDistrict.id.length)}
          nameType="ወረዳ"
          handleRestoreOk={handleRestoreOk}
        />
      )}
    </>
  );
}
